import { Injectable } from '@angular/core';
import { ErrorPolicyService, muteError, SanitizedError } from '@mri-platform/angular-error-handling';
import { UserScopedCacheService } from '@mri-platform/dsg/core';
import {
  AskAgoraDDChatDataset,
  AskAgoraReport,
  pbiDatasetQueries,
  PbiDatasetRepositoryService,
  pbiReportQueries,
  PbiReportRepositoryService,
  removeCategoryPrefix,
  ReportCategory
} from '@mri-platform/dsg/entity-state';
import { ChatDashboardSection } from '@mri-platform/shared/core';
import { retryBackoff } from 'backoff-rxjs';
import isEqual from 'lodash-es/isEqual';
import {
  concat,
  defaultIfEmpty,
  defer,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs';
import {
  ASK_AGORA_DATASETS_KEY,
  ASK_AGORA_KEY,
  ASK_AGORA_REPORTS_KEY,
  ONE_WEEK,
  THREE_MONTHS,
  validateAskAgoraDDChatDataset,
  validateAskAgoraProjectedReport,
  verifyValidItem
} from '../models';

@Injectable({ providedIn: 'root' })
export class AskAgoraService {
  constructor(
    private readonly pbiReportRepo: PbiReportRepositoryService,
    private readonly datasetRepo: PbiDatasetRepositoryService,
    readonly errorPolicy: ErrorPolicyService,
    private readonly cacheService: UserScopedCacheService
  ) {}

  getAskAgoraReportsAndDatasets$(ddChatEnabled: boolean) {
    return ddChatEnabled ? this.getDDChatFrameSources$() : this.getAskAgoraFrameSources$();
  }

  private buildAskAgoraEnabledFromCache$(): Observable<ChatDashboardSection | null> {
    return this.getFromCache<boolean>(ASK_AGORA_KEY).pipe(
      map(value => (value ? {} : null)),
      take(1),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private getAskAgoraFrameSources$() {
    const askAgoraEnabledFromCache$ = this.buildAskAgoraEnabledFromCache$();

    const getReportsFromCache$ = this.getFromCache<AskAgoraReport[]>(ASK_AGORA_REPORTS_KEY).pipe(
      switchMap(reports => {
        const isCacheValid = !reports || reports.every(validateAskAgoraProjectedReport);
        return isCacheValid
          ? of(reports)
          : of(null).pipe(tap(() => this.cacheService.removeItems(ASK_AGORA_REPORTS_KEY)));
      }),
      map(reports => this.transformReportsToFrameSources(reports)),
      withLatestFrom(askAgoraEnabledFromCache$),
      map(([currentFrameSources, defaultFrameSources]) => currentFrameSources ?? defaultFrameSources),
      take(1)
    );

    const getReportsFromServer$ = this.handleRetries(() =>
      this.pbiReportRepo.executeQuery(pbiReportQueries.selectAskAgoraReports)
    ).pipe(
      map(reports => reports as unknown as AskAgoraReport[] | SanitizedError),
      tap(reports => this.updateCacheOnResponse(reports, ASK_AGORA_REPORTS_KEY)),
      map(reports => this.transformReportsToFrameSources(reports)),
      take(1)
    );

    return concat(askAgoraEnabledFromCache$, getReportsFromCache$, getReportsFromServer$);
  }

  private getDDChatFrameSources$() {
    const askAgoraEnabledFromCache$ = this.buildAskAgoraEnabledFromCache$();

    const getDDChatDatasetsFromCache$ = this.getFromCache<AskAgoraDDChatDataset[]>(ASK_AGORA_DATASETS_KEY).pipe(
      switchMap(datasets => {
        const isCacheValid = !datasets || datasets.every(validateAskAgoraDDChatDataset);
        return isCacheValid
          ? of(datasets)
          : of(null).pipe(tap(() => this.cacheService.removeItems(ASK_AGORA_DATASETS_KEY)));
      }),
      map(datasets => this.transformDatasetsToFrameSources(datasets)),
      withLatestFrom(askAgoraEnabledFromCache$),
      map(([currentFrameSources, defaultFrameSources]) => currentFrameSources ?? defaultFrameSources),
      take(1)
    );

    const getDDChatDatasetsFromServer$ = this.handleRetries(() =>
      this.datasetRepo.executeQuery(pbiDatasetQueries.selectAskAgoraDatasets)
    ).pipe(
      map(datasets => datasets as unknown as AskAgoraDDChatDataset[] | SanitizedError),
      tap(datasets => this.updateCacheOnResponse(datasets, ASK_AGORA_DATASETS_KEY)),
      map(datasets => this.transformDatasetsToFrameSources(datasets)),
      take(1)
    );

    return concat(askAgoraEnabledFromCache$, getDDChatDatasetsFromCache$, getDDChatDatasetsFromServer$);
  }

  private getChatDashboardSectionFromReports(reports: AskAgoraReport[]): ChatDashboardSection {
    return reports
      .filter(report => report.report_Category?.startsWith(ReportCategory.AskAgora))
      .reduce<ChatDashboardSection>((result, report) => {
        const [, rawCategory, rawDisplayName] = report.report_Category.split('|');
        if (!rawCategory || !rawDisplayName) return result;

        // Remove the numeric prefix from both category and display name
        const category = removeCategoryPrefix(rawCategory);
        const displayName = removeCategoryPrefix(rawDisplayName);
        const embedAppUrl = `dashboards/${report.reportId}?filterPaneEnabled=false&authMode=tab`;
        return {
          ...result,
          [category]: {
            ...result[category],
            [displayName]: embedAppUrl
          }
        };
      }, {});
  }

  private getChatDashboardSectionFromDatasets(datasets: AskAgoraDDChatDataset[]): ChatDashboardSection {
    return datasets
      .filter(d => d.dataset_Category?.startsWith(ReportCategory.AskAgora))
      .reduce<ChatDashboardSection>((result, dataset) => {
        const [, rawCategory, rawDisplayName] = dataset.dataset_Category.split('|');
        if (!rawCategory || !rawDisplayName) return result;

        const category = removeCategoryPrefix(rawCategory);
        const displayName = removeCategoryPrefix(rawDisplayName);
        const url = `?templateWorkspaceName=${dataset.dataset_OrganisationWorkspace_TemplateWorkspace_Name}&datasetName=${dataset.name}&showLogo=false`;
        return {
          ...result,
          [category]: {
            ...result[category],
            [displayName]: url
          }
        };
      }, {});
  }

  private getFromCache<T>(key: string) {
    return from(this.cacheService.getItemAsync<T>(key)).pipe(this.errorPolicy.catchHandleReplace(null, muteError));
  }

  private handleRetries<T>(getEntities: () => Promise<T[]>) {
    return defer(getEntities).pipe(
      retryBackoff({ initialInterval: 1000, maxRetries: 3 }),
      this.errorPolicy.catchHandle(muteError),
      defaultIfEmpty([])
    );
  }

  private updateCacheOnResponse(
    items: AskAgoraReport[] | AskAgoraDDChatDataset[] | SanitizedError,
    itemsCacheKey: string
  ) {
    if (items instanceof SanitizedError) {
      this.cacheService.removeItems(ASK_AGORA_KEY, itemsCacheKey);
      return;
    }

    if (items.length === 0) {
      this.cacheService.removeItems(itemsCacheKey);
    }

    const itemsInCache = this.cacheService.getItem(itemsCacheKey);
    if (items.length !== 0 && !isEqual(items, itemsInCache)) {
      this.cacheService.setItem(itemsCacheKey, items, { ttl: ONE_WEEK });
    }

    this.cacheService.setItem(ASK_AGORA_KEY, items.length !== 0, { ttl: THREE_MONTHS });
  }

  private removeDuplicateUrls(chatDashboardSection: ChatDashboardSection): ChatDashboardSection {
    const uniqueUrls = new Set<string>();

    const filteredEntries = Object.entries(chatDashboardSection)
      .map(([key, val]) => [
        key,
        Object.fromEntries(
          Object.entries(val).filter(([_, reportUrl]) => {
            const isUnique = !uniqueUrls.has(reportUrl);
            if (isUnique) uniqueUrls.add(reportUrl);
            return isUnique;
          })
        )
      ])
      .filter(([_, val]) => Object.keys(val).length !== 0);
    return Object.fromEntries(filteredEntries);
  }

  private transformReportsToFrameSources(reports: AskAgoraReport[] | SanitizedError | null) {
    if (!verifyValidItem(reports)) {
      return null;
    }

    const chatDashboardSection = this.getChatDashboardSectionFromReports(reports);
    return this.removeDuplicateUrls(chatDashboardSection);
  }

  private transformDatasetsToFrameSources(datasets: AskAgoraDDChatDataset[] | SanitizedError | null) {
    if (!verifyValidItem(datasets)) {
      return null;
    }

    const chatDashboardSection = this.getChatDashboardSectionFromDatasets(datasets);
    return this.removeDuplicateUrls(chatDashboardSection);
  }
}
